import { type LineItem, type SubRate } from '@commercetools/platform-sdk'
import { jwtDecode } from 'jwt-decode'
import orderBy from 'lodash/orderBy'

import { configPublic } from '@src/config.public'
import type { DigitalCheckoutSchema } from '@src/constants/DigitalCheckoutFormSchema'
import type { ShippableCheckoutSchema } from '@src/constants/ShippableCheckoutFormSchema'
import { type TranslateFn } from '@src/i18n/LocaleProvider'
import { getLocaleFromCode } from '@src/i18n/locale.util'
import type { LocaleConfig } from '@src/i18n/locales'
import type { CreateOrderDraft, CreateOrderRequest } from '@src/services/order.service'
import { ServerActions } from '@src/services/server-actions.service'
import type {
  CybersourceToken,
  CybersourceTokenV2,
  PayerAuthCheckEnrollmentResponse,
  PayerAuthCommonParams,
  PayerAuthValidationResponse,
} from '@src/typings/Cybersource'
import { getCardExpirationDateForOrder, getCardExpirationMonthYear } from '@src/utils/date.util'
import { log } from '@src/utils/log.util'

import type { CardType } from './constants.util'
import { CyberSourceCardTypeMapping, PaymentConstants } from './constants.util'

export const TAX_NOT_CALCULATED = 'Not calculated'

const getPaymentFields = (args: {
  data: DigitalCheckoutSchema | ShippableCheckoutSchema
  payerAuthenticationTransactionId?: string
  payerAuthenticationTransaction?: PayerAuthCheckEnrollmentResponse | PayerAuthValidationResponse
}) => {
  const { data } = args

  if (data.checkoutType === 'credit-card') {
    const creditCardType = getCreditCardType(String(data?.cardToken))

    return {
      type: 'CreditCard' as const,
      creditCard: {
        creditCardType,
        transientToken: String(data?.cardToken),
        expirationDate: getCardExpirationDateForOrder(String(data?.cardExpiration)),
        payerAuthenticationTransactionId: args.payerAuthenticationTransactionId,
        payerAuthenticationTransaction: args.payerAuthenticationTransaction,
      },
    }
  }

  return {
    type: 'ApplePay' as const,
    applePay: {
      creditCardType: 'ApplePay',
      token: String(data?.applePayToken),
    },
  }
}

export function getCreditCardType(transientToken: string) {
  let creditCardType: CardType

  if (configPublic.featureFlags.microformV2Enabled) {
    const decodedToken: CybersourceTokenV2 = jwtDecode(transientToken)
    const cardTypeCode =
      decodedToken?.content?.paymentInformation?.card?.number?.detectedCardTypes?.[0] ?? ''
    creditCardType = CyberSourceCardTypeMapping[cardTypeCode] ?? ''
  } else {
    const decodedToken: CybersourceToken = jwtDecode(transientToken)
    const cardTypeCode = decodedToken?.data?.type ?? ''
    creditCardType = CyberSourceCardTypeMapping[cardTypeCode] ?? ''
  }

  return creditCardType
}

/** Returns the create cart request payload from the checkout form data. */
export function getOrderDraftFromCheckoutForm(args: {
  data: DigitalCheckoutSchema | ShippableCheckoutSchema
  locale: LocaleConfig
  custom?: CreateOrderRequest['orderDraft']['custom']
  payerAuthenticationTransactionId?: string
  payerAuthenticationTransaction?: PayerAuthCheckEnrollmentResponse | PayerAuthValidationResponse
}): CreateOrderDraft {
  const { data, locale, custom } = args

  const address =
    'isBillingAddressSameAsShipping' in data && data.isBillingAddressSameAsShipping
      ? data.shippingAddress
      : data.billingAddress
  const orderDraft = {
    billingAddress: {
      firstName: String(data?.cardFirstName),
      lastName: String(data?.cardLastName),
      streetName: String(address?.address),
      additionalStreetInfo: address?.addressNumber,
      city: String(address?.city),
      state: String(address?.state),
      postalCode: String(address?.zip),
      country: locale.countryCode,
    },
    payment: getPaymentFields(args),
    custom,
  }

  return orderDraft
}

/**
 * A processed cart/order object. Useful for sorting data, etc.
 * Avoid mutation on the data returned by the server.
 * @returns {Commercetools.Cart | Commercetools.Order}
 */
export function processCart<T extends Commercetools.Cart | Commercetools.Order>(cart: T | null) {
  if (!cart) return null
  return {
    ...cart,
    lineItems: orderBy(cart.lineItems, ['addedAt'], ['desc']),
  } as T
}

export async function getCommonPayerAuthParams(args: {
  orderDraft: CreateOrderDraft
}): Promise<PayerAuthCommonParams> {
  const { orderDraft } = args
  const cart = await ServerActions.serial.getCart()

  if (!cart) throw new Error('Cart is required to create Payer Auth params')

  const locale = getLocaleFromCode(String(cart.locale))
  const cardExpiration = getCardExpirationMonthYear(orderDraft.payment.creditCard?.expirationDate)
  const cartAmounts = parseCartAmounts(cart, () => '')
  const totalAmount = Math.max(cartAmounts.total, 0) || PaymentConstants.ONE_DOLLAR_CENT_AMOUNT

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  return {
    clientReferenceInformation: {
      code: cart.custom?.fields?.orderNumber ?? '',
    },
    tokenInformation: {
      transientTokenJwt: String(orderDraft.payment.creditCard?.transientToken),
    },
    paymentInformation: {
      card: {
        expirationMonth: cardExpiration.month,
        expirationYear: cardExpiration.year,
      },
    },
    orderInformation: {
      amountDetails: {
        totalAmount: (totalAmount / 100).toFixed(2),
        currency: cart.totalPrice.currencyCode,
      },
      billTo: {
        firstName: orderDraft.billingAddress.firstName,
        lastName: orderDraft.billingAddress.lastName,
        email: cart.customerEmail || PaymentConstants.EMPTY_VALUE,
        address1: orderDraft.billingAddress.streetName || PaymentConstants.EMPTY_VALUE,
        address2: orderDraft.billingAddress.additionalStreetInfo || PaymentConstants.EMPTY_VALUE,
        locality: orderDraft.billingAddress.city || PaymentConstants.EMPTY_VALUE,
        administrativeArea:
          (locale?.payerAuthAdministrativeAreaHasCountry
            ? `${orderDraft.billingAddress.country}-${orderDraft.billingAddress.state}`
            : orderDraft.billingAddress.state) || PaymentConstants.EMPTY_VALUE,
        country: orderDraft.billingAddress.country || PaymentConstants.EMPTY_VALUE,
        postalCode: orderDraft.billingAddress.postalCode || PaymentConstants.EMPTY_VALUE,
      },
    },
  }
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
}

export function isCart(value: any): value is Commercetools.Cart {
  return value && typeof value === 'object' && 'lineItems' in value
}

export function getLineItemDiscounts(lineItem: Commercetools.LineItem | null) {
  if (!lineItem) return 0
  const priceBeforeDiscounts = lineItem.price.value.centAmount * lineItem.quantity
  const priceAfterDiscounts = lineItem.totalPrice.centAmount
  const totalLineItemDiscounts = priceBeforeDiscounts - priceAfterDiscounts

  return totalLineItemDiscounts
}

export function getTotalDiscounts(cart: Commercetools.Cart | Commercetools.Order | null) {
  if (!cart?.lineItems) return 0

  const totalCartDiscounts = cart?.lineItems.reduce((acc, lineItem: Commercetools.LineItem) => {
    const totalLineItemDiscounts = getLineItemDiscounts(lineItem)
    return acc + totalLineItemDiscounts
  }, 0)

  const shipping = cart?.shippingInfo?.price?.centAmount ?? 0
  const shippingDiscounted = cart?.shippingInfo?.discountedPrice?.value?.centAmount

  const shippingDiscounts = shippingDiscounted !== undefined ? shipping - shippingDiscounted : 0

  return totalCartDiscounts + shippingDiscounts
}

/** Returns numeric and formatted amounts for a cart/order. */
export function getCartAmounts(cart: Commercetools.Cart | Commercetools.Order | null) {
  const subtotal = cart?.taxedPrice?.totalNet?.centAmount ?? cart?.totalPrice?.centAmount ?? 0
  const shippingBase = cart?.shippingInfo?.price?.centAmount ?? 0
  const shippingDiscounted = cart?.shippingInfo?.discountedPrice?.value?.centAmount
  const shipping = shippingDiscounted ?? shippingBase

  return {
    isTaxCalculated: Boolean(cart?.taxedPrice),
    totalDiscounts: getTotalDiscounts(cart),
    subtotal,
    shipping,
    shippingBase,
    shippingDiscounted,
    subtotalNoShipping: subtotal - shipping,
    total: cart?.taxedPrice?.totalGross?.centAmount ?? cart?.totalPrice?.centAmount ?? 0,
    estimatedTax: cart?.taxedPrice?.totalTax?.centAmount ?? 0,
    lineItems: cart?.lineItems.map(getLineItemAmounts) ?? [],
  }
}

/** Returns numeric and formatted amounts for a cart/order. */
export function parseCartAmounts(
  cart: Commercetools.Cart | Commercetools.Order | null,
  t: TranslateFn
) {
  const amounts = getCartAmounts(cart)
  const GST_HST =
    cart?.taxedPrice?.taxPortions.find(
      (portion) => portion.name === 'HST' || portion.name === 'GST'
    )?.amount.centAmount ?? 0
  const PST_QST_RST =
    cart?.taxedPrice?.taxPortions.find(
      (portion) => portion.name === 'PST' || portion.name === 'QST' || portion.name === 'RST'
    )?.amount.centAmount ?? 0
  const lineItems = parseLineItemAmounts(cart?.lineItems ?? [], t)

  return {
    ...amounts,
    subtotalFormatted: formatCartAmount(amounts.subtotal, t),
    subtotalNoShippingFormatted: formatCartAmount(amounts.subtotalNoShipping, t),
    totalFormatted: formatCartAmount(amounts.total, t),
    totalDiscountsFormatted:
      amounts.totalDiscounts > 0 ? formatCartAmount(amounts.totalDiscounts, t) : undefined,
    shippingFormatted: formatCartAmount(amounts.shipping, t),
    shippingBaseFormatted: formatCartAmount(amounts.shippingBase, t),
    shippingDiscountedFormatted:
      amounts.shippingDiscounted !== undefined
        ? formatCartAmount(amounts.shippingDiscounted, t)
        : undefined,
    estimatedTaxFormatted: amounts.isTaxCalculated
      ? formatCartAmount(amounts.estimatedTax, t)
      : TAX_NOT_CALCULATED,
    estimatedTaxGST_HSTFormatted: amounts.isTaxCalculated
      ? formatCartAmount(GST_HST, t)
      : TAX_NOT_CALCULATED,
    estimatedTaxPST_QST_RSTFormatted: amounts.isTaxCalculated
      ? formatCartAmount(PST_QST_RST, t)
      : TAX_NOT_CALCULATED,
    estimatedTax: amounts.isTaxCalculated ? amounts.estimatedTax : TAX_NOT_CALCULATED,
    estimatedTaxGST_HST: amounts.isTaxCalculated ? GST_HST : TAX_NOT_CALCULATED,
    estimatedTaxPST_QST_RST: amounts.isTaxCalculated ? PST_QST_RST : TAX_NOT_CALCULATED,
    lineItems,
  }
}

/** Returns numeric amounts for the line items in a cart. */
export function getLineItemAmounts(lineItem: Commercetools.LineItem) {
  const { taxedPrice, totalPrice, price } = lineItem

  return {
    isTaxCalculated: Boolean(taxedPrice),
    subtotal: taxedPrice?.totalNet?.centAmount ?? totalPrice?.centAmount ?? 0,
    total: taxedPrice?.totalGross?.centAmount ?? totalPrice?.centAmount ?? 0,
    estimatedTax: taxedPrice?.totalTax?.centAmount ?? 0,
    originalPrice: price.value.centAmount,
  }
}

/** Returns numeric and formatted amounts for the line items in a cart. */
export function parseLineItemAmounts(lineItems: Commercetools.LineItem[] | null, t: TranslateFn) {
  if (!lineItems) return []

  return lineItems.map((lineItem) => {
    const amounts = getLineItemAmounts(lineItem)

    return {
      ...amounts,
      subtotalFormatted: formatCartAmount(amounts.subtotal, t),
      totalFormatted: formatCartAmount(amounts.total, t),
      estimatedTaxFormatted: amounts.isTaxCalculated
        ? formatCartAmount(amounts.estimatedTax, t)
        : TAX_NOT_CALCULATED,
      originalPriceFormatted: formatCartAmount(amounts.originalPrice, t),
    }
  })
}

// Returns a formatted amount for display.
export function formatCartAmount(amount: number, t: TranslateFn) {
  return t('currency', { amount: (amount / 100).toFixed(2) })
}

// Convert cents to dollars and return as a number with up to 2 decimal points.
export function convertCentsToFloat(amount: number) {
  return parseFloat((amount / 100).toFixed(2))
}

export function getLineItemName(lineItem: Commercetools.LineItem, localeCode: string = 'en-US') {
  const name = lineItem.name
  return name[localeCode] ?? ''
}

// Returns an custom line item attribute value by the attribute name.
export function getLineItemAttribute(
  lineItem: Commercetools.LineItem,
  attributeName: string,
  localeCode?: string
) {
  const attribute = lineItem?.variant?.attributes?.find(
    (attribute: any) => attribute.name === attributeName
  )
  if (localeCode) return attribute?.value?.[localeCode] ?? ''
  return attribute?.value ?? ''
}

export function isDigitalLineItem(lineItem: Commercetools.LineItem) {
  const attribute = lineItem?.variant?.attributes?.find(
    (attribute: any) => attribute.name === 'distributionMethod'
  )
  return attribute?.value === 'Digital'
}

export function checkIsDigitalOrder(order: Commercetools.Order | null): boolean {
  if (!order) return false
  return order.lineItems.every((lineItem) => {
    return (
      lineItem.variant.attributes?.find((attribute) => attribute.name === 'distributionMethod')
        ?.value === 'Digital'
    )
  })
}

// Returns card information from the order.
export function parsePaymentInfo(order: Commercetools.Order | null) {
  const cardBrand = order?.paymentInfo?.payments?.[0]?.obj?.custom?.fields?.creditCardType
  const cardToken = order?.paymentInfo?.payments?.[0]?.obj?.custom?.fields?.transientToken
  const isApplePay = cardBrand === 'APPLE PAY'
  let card: Commercetools.CardToken | null = null

  try {
    card = cardToken && !isApplePay ? jwtDecode(cardToken) : null
  } catch (error: any) {
    log.error(`Error parsing card token - ${error?.message}`, error)
  }

  return {
    isApplePay,
    cardBrand: cardBrand ?? '',
    cardLast4: card?.content?.paymentInformation.card.number.maskedValue.slice(-4) ?? '',
    cardExpiration: new Date(
      order?.paymentInfo?.payments?.[0]?.obj?.custom?.fields?.expirationDate ?? 0
    ).toLocaleDateString('en-US', {
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }),
  }
}

export function getShippableProducts({ cart }: { cart?: Commercetools.Cart }) {
  const items = cart?.lineItems ?? []
  const shippableProducts = items.filter((item) => {
    const productAttributes = item?.variant?.attributes ?? []
    const nonDigitalProduct = productAttributes.find(
      (att) => att.name === 'distributionMethod' && att.value === 'Delivery'
    )
    return nonDigitalProduct
  })
  return shippableProducts
}

export function isLineItemShippable({ lineItem }: { lineItem: Commercetools.LineItem }) {
  const productAttributes = lineItem?.variant?.attributes ?? []
  const isShippable = productAttributes.some(
    (att) => att.name === 'distributionMethod' && att.value === 'Delivery'
  )

  return isShippable
}

export function getHasExistingPartner(
  cartOrOrder?: Commercetools.Cart | Commercetools.Order | null
) {
  if (!cartOrOrder) return false
  return cartOrOrder?.custom?.fields?.hasExistingPartner === true
}

/** Returns a set of Disclaimers from line items. */
export function parseDisclaimers(lineItems?: Commercetools.LineItem[] | null, localeCode?: string) {
  const disclaimersMap = new Map<string, Commercetools.Disclaimer>()

  lineItems?.forEach((lineItem) => {
    const disclaimersList = lineItem?.variant?.attributes?.find((a) => a?.name === 'disclaimers')
    disclaimersList?.value?.forEach((disclaimer: any) => {
      disclaimersMap.set(disclaimer?.obj?.key ?? '', {
        id: disclaimer?.obj?.id ?? '',
        key: disclaimer?.obj?.key ?? '',
        priority: disclaimer?.obj?.value?.priority ?? 0,
        content: localeCode ? (disclaimer?.obj?.value?.content?.[localeCode] ?? '') : '',
      })
    })
  })

  const disclaimers = Array.from(disclaimersMap.values()).sort((a, b) => a.priority - b.priority)
  return disclaimers
}

/** Returns a set of Guarantees from the cart line items. */
export function parseGuarantees(cart?: Commercetools.Cart | null, localeCode?: string) {
  const guaranteesMap = new Map<string, Commercetools.Guarantee>()

  cart?.lineItems?.forEach((lineItem) => {
    const guaranteesList = lineItem?.variant?.attributes?.find((a) => a?.name === 'guarantees')
    guaranteesList?.value?.forEach((guarantee: any) => {
      guaranteesMap.set(guarantee?.obj?.key ?? '', {
        id: guarantee?.obj?.id ?? '',
        key: guarantee?.obj?.key ?? '',
        priority: guarantee?.obj?.value?.priority ?? 0,
        content: localeCode ? (guarantee?.obj?.value?.content?.[localeCode] ?? '') : '',
        icon: localeCode ? (guarantee?.obj?.value?.icon?.[localeCode] ?? '') : '',
      })
    })
  })

  const guarantees = Array.from(guaranteesMap.values()).sort((a, b) => a.priority - b.priority)
  return guarantees
}

/** Returns a set of applied discounts with the ammount formatted */
export function parseDiscounts(
  cart: Commercetools.Cart | null,
  localeCode: string,
  t: TranslateFn
) {
  return cart?.discountOnTotalPrice?.includedDiscounts.map(({ discount, discountedAmount }) => ({
    name: (discount as Commercetools.Discount).obj?.name[localeCode],
    discountedFormatted: formatCartAmount(discountedAmount.centAmount, t),
  }))
}

export const getCanadianTaxLabel = (lineItem: LineItem | undefined, localeCode = 'en-CA') => {
  const locale = getLocaleFromCode(localeCode)
  const labelNames = lineItem?.taxRate?.subRates?.map((item: SubRate) => item?.name)
  return labelNames?.map((name) => (locale.subrateLabels as Record<any, any>)[name]).join(', ')
}

export function getComputedDetailsLabels(lineItem: LineItem | undefined, localeCode: string) {
  const computedDetails: Commercetools.ComputedDetails | undefined =
    lineItem?.variant?.attributes?.find((attribute: any) => attribute.name === 'computedDetails')

  const labels = (computedDetails?.value ?? []).map((detail) => detail?.value?.[localeCode] ?? '')
  return labels
}

/**
 * Returns the images for a line item, filtered by the tags passed in.
 * @param lineItem the line item to get the images for
 * @param options.tags the tags to filter the images by
 * @param options.fallbackTags the default tags to filter the fallback images
 * @returns
 */
export function getLineItemImages(
  lineItem: Commercetools.LineItem,
  localeCode: string = 'en-US',
  options = { tags: ['default'], fallbackTags: ['default'] }
): Array<{
  src: string
  alt: string
  tags: string[]
}> {
  const { assets } = lineItem.variant
  if (!assets) return []

  const primaryAssets = assets.filter(({ tags }) =>
    options.tags.every((tag) => tags?.includes(tag))
  )

  const fallbackAssets = assets.filter(({ tags }) =>
    options.fallbackTags.every((tag) => tags?.includes(tag))
  )

  return (primaryAssets.length ? primaryAssets : fallbackAssets).map(
    ({ sources, custom, tags }) => ({
      src: sources[0].uri ?? '',
      alt: custom?.fields.altText[localeCode] ?? '',
      tags: tags ?? [],
    })
  )
}

/** Returns whether a cart line item is for a subscription. */
export function getIsSubscriptionLineItem(lineItem: Commercetools.LineItem) {
  return getLineItemAttribute(lineItem, 'productSubtype') === 'Subscription'
}

/** Returns whether a cart has a subscription line item. */
export function getIsSubscriptionInLineItems(lineItems: Commercetools.LineItem[]) {
  return lineItems.some(getIsSubscriptionLineItem)
}

/** Returns whether a cart line item is for a digital purchase. */
export function getIsDigitalPurchaseLineItem(lineItem: Commercetools.LineItem) {
  return getLineItemAttribute(lineItem, 'productSubtype') === 'DigitalPurchase'
}

/** Returns digital purchase line items in the cart. */
export function getDigitalPurchaseLineItems(lineItems: Commercetools.LineItem[]) {
  return lineItems.filter(getIsDigitalPurchaseLineItem)
}

/** Returns a subset of the lineItem data. Useful for storing in cookies */
export function getLineItemSubset(lineItem: Commercetools.LineItem): Commercetools.LineItemSubset {
  return {
    lineItemId: lineItem.id,
    quantity: lineItem.quantity,
    sku: lineItem.variant.sku ?? '',
  }
}

/** Returns the line items from the cart that match the given SKUs. */
export function getLineItemsFromSKUs(
  cart: Commercetools.Cart | null,
  skus: string[]
): Commercetools.LineItem[] {
  return cart?.lineItems?.filter((item) => skus.includes(item.variant.sku ?? '')) ?? []
}

/** Returns the line items from the cart that match the given Product Key. */
export function getLineItemsFromProductKeys(
  cart: Commercetools.Cart | null,
  productKeys: string[]
): Commercetools.LineItem[] {
  return cart?.lineItems?.filter((item) => productKeys.includes(item.productKey ?? '')) ?? []
}

export function getCartHasLineItem(
  cart: Commercetools.Cart | null,
  lineItemSubset: Commercetools.LineItemSubset
) {
  return cart?.lineItems.some((i) => i.variant.sku === lineItemSubset.sku)
}

/** Removes PII data in the Checkount Form state for logging */
export function getFormDataLog(data: DigitalCheckoutSchema | ShippableCheckoutSchema) {
  const { cardToken, cardExpiration, billingAddress, shippingAddress, ...logData } = data
  return logData
}

export function getCartQuantity(cart: Commercetools.Cart | null) {
  return cart?.lineItems.reduce((acc, item) => acc + item.quantity, 0) ?? 0
}

/** Returns line items with errors. */
export function getLineItemsWithErrors(cart: Commercetools.Cart | null) {
  return cart?.lineItems.filter((lineItem) => lineItem?.custom?.fields?.errors?.length > 0) ?? []
}
